import { lazy, Suspense } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ArrowUpRight, Calendar, Clock } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { blogPosts } from '@/data/blog'
import { formatDate } from '@/lib/utils'
import { useSeo } from '@/hooks/useSeo'
import { trackEvent } from '@/lib/analytics'

const MarkdownArticle = lazy(() =>
  import('@/components/MarkdownArticle').then((m) => ({
    default: m.MarkdownArticle,
  }))
)

// The page header already renders the post title; strip a leading markdown H1
// from the body so it isn't shown twice.
const stripLeadingH1 = (content: string): string =>
  content.replace(/^\s*#\s+.+\n+/, '')

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>()
  const post = blogPosts.find((p) => p.slug === slug)
  useSeo(post ? post.title : 'Post Not Found', post?.excerpt)

  if (!post) {
    return (
      <div className="container flex max-w-3xl flex-col items-center py-32 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">Post not found</h1>
        <Button asChild className="mt-6">
          <Link to="/blog">
            <ArrowLeft className="h-4 w-4" />
            Back to Writing
          </Link>
        </Button>
      </div>
    )
  }

  const related = blogPosts
    .filter(
      (p) =>
        p.id !== post.id &&
        (p.category === post.category ||
          p.tags.some((t) => post.tags.includes(t)))
    )
    .slice(0, 2)

  return (
    <article className="container max-w-2xl pb-20 pt-12">
      <Button asChild variant="ghost" size="sm" className="-ml-3 mb-6">
        <Link to="/blog">
          <ArrowLeft className="h-3.5 w-3.5" />
          Writing
        </Link>
      </Button>

      <header className="border-b border-border/40 pb-8">
        <Badge variant="outline" className="font-normal">
          {post.category}
        </Badge>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
          {post.title}
        </h1>
        <p className="mt-3 text-base text-muted-foreground">{post.excerpt}</p>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1 tabular-nums">
            <Calendar className="h-3 w-3" />
            {formatDate(post.date)}
          </span>
          {post.readTime > 0 && (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {post.readTime} min read
            </span>
          )}
        </div>
      </header>

      <div className="py-10">
        <Suspense
          fallback={
            <div className="py-10 text-sm text-muted-foreground">Loading…</div>
          }
        >
          <MarkdownArticle content={stripLeadingH1(post.content)} />
        </Suspense>
      </div>

      {post.externalUrl && (
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Button asChild>
            <a
              href={post.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('external_link_click', { post_slug: post.slug })}
            >
              Read the full report
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </Button>
          <span className="break-all text-xs text-muted-foreground">
            {post.externalUrl}
          </span>
        </div>
      )}

      {post.tags.length > 0 && (
        <div className="mt-10 flex flex-wrap gap-1.5 border-t border-border/40 pt-6">
          {post.tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="font-normal">
              {tag}
            </Badge>
          ))}
        </div>
      )}

      {related.length > 0 && (
        <section className="mt-16 border-t border-border/40 pt-8">
          <h2 className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Related
          </h2>
          <ul className="divide-y divide-border/60">
            {related.map((p) => (
              <li key={p.id} className="py-4">
                <Link to={`/blog/${p.slug}`} className="group block">
                  <h3 className="text-sm font-medium transition-colors group-hover:text-primary">
                    {p.title}
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {p.excerpt}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  )
}
